import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { LogOut } from "lucide-react";

export default function Header() {
    const { user, logout } = useAuth();
    const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase();

    return (
        <header
            className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-[#E5E5E3]"
            data-testid="app-header"
        >
            <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
                <div className="flex items-center gap-10">
                    <NavLink to="/calendar" className="flex items-center gap-3" data-testid="header-logo">
                        <img
                            src="https://customer-assets.emergentagent.com/job_group-calendar-7/artifacts/qy8qz9tb_SF%20WF%20logo%20%281%29.png"
                            alt="Scratchfirst"
                            className="h-8 w-auto"
                        />
                        <span className="hidden sm:inline text-[10px] tracking-[0.2em] text-[#888885] uppercase pl-3 border-l border-[#E5E5E3]">calendar</span>
                    </NavLink>
                    <nav className="hidden md:flex items-center gap-1">
                        <NavItem to="/calendar" testId="nav-calendar">Calendar</NavItem>
                        <NavItem to="/my" testId="nav-my">My activities</NavItem>
                        <NavItem to="/contacts" testId="nav-contacts">Contacts</NavItem>
                    </nav>
                </div>

                <div className="flex items-center gap-4">
                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none focus:ring-1 focus:ring-[#111110] focus:ring-offset-2 focus:ring-offset-[#F9F9F8]">
                            <Avatar className="w-8 h-8 rounded-none border border-[#E5E5E3]">
                                <AvatarImage src={user?.picture} alt={user?.name} />
                                <AvatarFallback className="rounded-none bg-[#F0F0EE] text-[#111110] text-xs font-medium">
                                    {initials}
                                </AvatarFallback>
                            </Avatar>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none border-[#E5E5E3] w-64 shadow-none">
                            <DropdownMenuLabel className="py-3">
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-[#111110]" data-testid="user-menu-name">{user?.name}</span>
                                    <span className="text-xs text-[#888885] font-normal mt-0.5" data-testid="user-menu-email">{user?.email}</span>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-[#E5E5E3]" />
                            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="rounded-none cursor-pointer text-[#111110]">
                                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Mobile nav */}
            <nav className="md:hidden flex items-center gap-1 px-4 h-11 border-t border-[#E5E5E3] bg-white/60">
                <NavItem to="/calendar" testId="nav-calendar-mobile">Calendar</NavItem>
                <NavItem to="/my" testId="nav-my-mobile">My activities</NavItem>
                <NavItem to="/contacts" testId="nav-contacts-mobile">Contacts</NavItem>
            </nav>
        </header>
    );
}

function NavItem({ to, children, testId }) {
    return (
        <NavLink
            to={to}
            data-testid={testId}
            className={({ isActive }) =>
                `px-3 h-9 inline-flex items-center text-sm tracking-tight transition-colors ${
                    isActive ? "text-[#111110] border-b border-[#111110]" : "text-[#666664] hover:text-[#111110]"
                }`
            }
        >
            {children}
        </NavLink>
    );
}
